/**
 * PURE echarts option builder for the STACKED_BAR chart (also drives its `line` / `area` variants).
 *
 * Kept separate from the component so a future server-side render path can reuse the same option
 * builder. This module is React/DOM/Redux-free: its only runtime imports are the pure `vizFormatters`. All
 * echarts imports are TYPE-ONLY (erased at build), so nothing from echarts' runtime is pulled in.
 *
 * Keep every visual detail byte-identical to the component (palette, stacking, tooltip HTML, axis
 * wrapping/rotation, percent scaling) and keep `animation:false` — mandatory for SSR.
 *
 * The tooltip formatter highlights the currently-hovered series. In the live UI that state lives in a
 * React ref; the builder receives it as a plain `getHoveredSeries` accessor (defaulting to `() => null`,
 * which is correct for SSR where there is no hover).
 */
import type { ComposeOption } from 'echarts/core'
import type { BarSeriesOption, CustomSeriesOption, LineSeriesOption } from 'echarts/charts'
import type { GridComponentOption, LegendComponentOption, TooltipComponentOption } from 'echarts/components'
import type { XAXisComponentOption, YAXisComponentOption } from 'echarts'
import type { TopLevelFormatterParams } from 'echarts/types/dist/shared'
import { escapeHtml, visTickFormatter } from './vizFormatters.js'
import type { StackSeries } from '../StackSeries.ts'
import { CHART_SURFACE, fillStyleFor, identityFor, identityKeySvg, lineStyleFor } from './visualIdentity.js'

export type StackedBarEChartsOption = ComposeOption<
  | BarSeriesOption
  | CustomSeriesOption
  | LineSeriesOption
  | GridComponentOption
  | LegendComponentOption
  | TooltipComponentOption
  | XAXisComponentOption
  | YAXisComponentOption
>

export interface StackedBarOptionInput {
  data: unknown[]
  stackSeries: StackSeries[]
  /** Row field to read category labels from (e.g. "xAxisvalue"); supports dotted paths. */
  dataKey: string
  ylabel: string
  xlabel: string
  xTickRotate?: number
  xTickMaxChars?: number
  isPercent?: boolean
  /** Series style. `'bar'` (default) keeps stacked-bar behaviour; `'line'`/`'area'` render the same
   *  stacked data as a line / filled-area chart (shared axes, tooltip, click). */
  variant?: 'bar' | 'line' | 'area'
  /** Live accessor for the hovered series' short name (tooltip row highlight). Defaults to `() => null`. */
  getHoveredSeries?: () => string | null
  /**
   * Samples per bucket, positionally aligned to `data`, surfaced in the tooltip.
   *
   * Aligned by index rather than keyed by label because two buckets can flatten to the same axis
   * text — more easily now that the label's own count is stripped — and echarts hands the formatter
   * an exact `dataIndex`. `undefined` for an entry means the count is unknown (no bucket stood behind
   * the row) and the line is left out; `0` means the bucket is genuinely empty and is shown.
   */
  sampleCounts?: (number | undefined)[]
}

type AnyRec = Record<string, unknown>

const LINE_WIDTH = 2

/** Past this many bars they are only a few px wide, and separators would cut them into speckle. */
const SEGMENT_GAP_MAX_BARS = 40
/** Under ~0.5% of the axis a segment is a few px tall; a separator against it would read as a white gap. */
const SEGMENT_GAP_MIN_SHARE = 0.005
/** echarts' default for a single stack (a 31% barCategoryGap), set explicitly so separators span exactly the bar. */
const BAR_SHARE_OF_CATEGORY = 0.69

/**
 * Where one full-size segment meets the next: `[categoryIndex, stacked value]`. Separators are drawn as lines over
 * the bars rather than as segment outlines, because an outline narrows its segment and any segment without one then
 * sticks out sideways.
 */
export function segmentSeparators(columns: number[][], axisMax: number): Array<[number, number]> {
  const out: Array<[number, number]> = []
  columns.forEach((values, category) => {
    let stacked = 0
    let belowIsFullSize = false
    for (const value of values) {
      if (!(value > 0)) continue
      const fullSize = value / axisMax >= SEGMENT_GAP_MIN_SHARE
      if (fullSize && belowIsFullSize) out.push([category, stacked])
      stacked += value
      belowIsFullSize = fullSize
    }
  })
  return out
}

function getPath(obj: AnyRec, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, k) => (acc == null ? acc : (acc as Record<string, unknown>)[k]), obj)
}

/**
 * Wrap an axis label to `maxChars`, breaking between words rather than mid-token.
 *
 * Chunking blindly every `maxChars` split the bucket's member count across lines — `Mar 2026 [6]`
 * came out as `Mar 2026 [` / `6]` — which is what made a date axis look ragged next to the filled
 * buckets. A single word longer than the limit is still hard-split; there is nowhere else to break it.
 */
function wrapLabel(s: string, maxChars = 0) {
  if (!maxChars || maxChars <= 0 || typeof s !== 'string') return s

  const hardSplit = (word: string) => {
    const parts: string[] = []
    for (let i = 0; i < word.length; i += maxChars) parts.push(word.slice(i, i + maxChars))
    return parts
  }

  const lines: string[] = []
  for (const word of s.split(/\s+/).filter(Boolean)) {
    const last = lines[lines.length - 1]
    if (last !== undefined && last.length + 1 + word.length <= maxChars) {
      lines[lines.length - 1] = `${last} ${word}`
    } else if (word.length > maxChars) {
      lines.push(...hardSplit(word))
    } else {
      lines.push(word)
    }
  }
  return lines.join('\n')
}

/** Build the echarts option for a stacked-bar (or line/area) chart. Pure `input -> option` map. */
export function stackedBarOption(input: StackedBarOptionInput): StackedBarEChartsOption {
  const {
    data,
    stackSeries,
    dataKey,
    ylabel,
    xlabel,
    xTickRotate = 90,
    xTickMaxChars = 10,
    isPercent = false,
    variant = 'bar',
    getHoveredSeries = () => null,
    sampleCounts
  } = input

  const safeData = Array.isArray(data) ? (data as AnyRec[]) : []

  const categories = safeData.map((d) => String(getPath(d, dataKey) ?? ''))

  const isBar = variant === 'bar'
  const segmentGaps = isBar && safeData.length <= SEGMENT_GAP_MAX_BARS
  const stackTotals = safeData.map((row) => stackSeries.reduce((sum, s) => sum + (Number(s.value(row)) || 0), 0))
  const axisMax = isPercent ? 100 : Math.max(1, ...stackTotals)
  const series: Array<BarSeriesOption | LineSeriesOption> = stackSeries.map((s) => {
    const vals = safeData.map((row) => s.value(row))

    const identity = identityFor(s.label)
    const common = {
      id: s.id,
      name: s.label,
      stack: variant === 'line' ? undefined : 'total',
      data: vals,
      emphasis: { focus: 'series' as const },
      blur: { itemStyle: { opacity: 0.15 } },
      itemStyle: fillStyleFor(identity)
    }

    if (isBar) {
      return {
        ...common,
        type: 'bar',
        large: true,
        barCategoryGap: `${Math.round((1 - BAR_SHARE_OF_CATEGORY) * 100)}%`,
        data: vals.map((v) => ({ value: v, itemStyle: fillStyleFor(identity, (Number(v) || 0) / axisMax) })),
        itemStyle: { color: identity.color }
      } as BarSeriesOption
    }
    return {
      ...common,
      type: 'line',
      smooth: false,
      showSymbol: false,
      lineStyle: lineStyleFor(identity, LINE_WIDTH),
      ...(variant === 'area' ? { areaStyle: { opacity: 0.55 } } : {})
    } as LineSeriesOption
  })

  // A surface-coloured line where two full-size segments touch, so two similar colours still read as two segments.
  const separators: CustomSeriesOption | null = segmentGaps
    ? {
        type: 'custom',
        id: 'segment-separators',
        silent: true,
        z: 3,
        tooltip: { show: false },
        data: segmentSeparators(
          safeData.map((row) => stackSeries.map((s) => Number(s.value(row)) || 0)),
          axisMax
        ),
        renderItem: (_params, api) => {
          const [x, y] = api.coord([api.value(0), api.value(1)]) as [number, number]
          const half = ((api.size?.([1, 0]) as number[])[0] * BAR_SHARE_OF_CATEGORY) / 2
          // Half-pixel offset keeps the 1px line on one pixel row instead of smeared across two.
          const row = Math.round(y) + 0.5
          return { type: 'line', shape: { x1: x - half, x2: x + half, y1: row, y2: row }, style: { stroke: CHART_SURFACE, lineWidth: 1 } }
        }
      }
    : null

  const bottom = xTickRotate ? 140 : 80

  return {
    backgroundColor: CHART_SURFACE,
    animation: false,

    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      confine: true,
      renderMode: 'html',

      formatter: (params: TopLevelFormatterParams) => {
        const items = Array.isArray(params) ? params : [params]
        const axisLabel = items[0]?.name ?? ''
        const hovered = getHoveredSeries()
        const samples = sampleCounts?.[items[0]?.dataIndex ?? -1]

        const rows = items
          .map((p) => ({
            name: String(p.seriesName ?? ''),
            value: Number(p.value ?? 0)
          }))
          .filter((a) => a.value > 0)
          .sort((a, b) => b.value - a.value)

        const htmlRows = rows
          .map((r) => {
            const hot = hovered && r.name === hovered
            const pretty = visTickFormatter?.(r.value) ?? String(r.value)

            return `
                <div style="
                  display:flex;justify-content:space-between;gap:12px;margin:2px 0;
                  padding:2px 6px;border-radius:8px;
                  background:${hot ? 'rgba(0,0,0,0.10)' : 'transparent'};
                  font-weight:${hot ? 700 : 400};
                ">
                  <div style="display:flex;align-items:center;gap:6px;min-width:0;">
                    ${identityKeySvg(identityFor(r.name), { size: 10, lineLength: variant === 'line' ? 22 : 0 })}
                    <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(r.name)}</span>
                  </div>
                  <span style="font-variant-numeric:tabular-nums;">${escapeHtml(String(pretty))}</span>
                </div>
              `
          })
          .join('')

        const sampleLine =
          samples === undefined
            ? ''
            : `<div style="opacity:0.65;margin:-4px 0 6px">${samples} ${samples === 1 ? 'sample' : 'samples'}</div>`

        return `
            <div style="min-width:240px">
              <div style="font-weight:700;margin-bottom:6px">${escapeHtml(String(axisLabel))}</div>
              ${sampleLine}
              ${htmlRows}
            </div>
          `
      }
    },

    legend: { top: 0, show: false },

    grid: {
      top: 30,
      bottom,
      left: 60,
      right: 30,
      containLabel: true
    },

    xAxis: {
      type: 'category',
      name: xlabel,
      nameLocation: 'middle',
      nameGap: 55,
      data: categories,

      axisLabel: {
        rotate: xTickRotate,
        interval: 0, // show every tick, none skipped
        hideOverlap: true,
        formatter: (val: string) => wrapLabel(val, xTickMaxChars)
      },

      axisTick: {
        interval: 0,
        alignWithLabel: true
      }
    },

    yAxis: {
      type: 'value',
      name: ylabel,
      nameLocation: 'middle',
      nameGap: 50,
      min: 0,
      max: isPercent ? 100 : undefined,
      interval: isPercent ? 20 : undefined,
      axisLabel: {
        formatter: (val: number) => visTickFormatter?.(val) ?? String(val)
      }
    },

    series: separators ? [...series, separators] : series,
    progressive: 500
  }
}
